/**
 * Preptera blog articles — infinite scroll via Section Rendering API.
 * Page 1 is server-rendered; subsequent pages append grid cards only.
 */
(function () {
  const SELECTOR = '[data-blog-articles]';

  class PrepteraBlogArticles {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('[data-blog-articles-grid]');
      this.sentinel = root.querySelector('[data-blog-articles-sentinel]');
      this.loader = root.querySelector('[data-blog-articles-loader]');
      this.status = root.querySelector('[data-blog-articles-status]');

      this.sectionId = root.dataset.sectionId;
      this.blogPath = root.dataset.blogPath;
      this.featuredHandle = (root.dataset.featuredArticleHandle || '').split('/').pop();
      this.currentPage = Number.parseInt(root.dataset.currentPage, 10) || 1;
      this.totalPages = Number.parseInt(root.dataset.totalPages, 10) || 1;

      this.loading = false;
      this.observer = null;

      if (!this.grid || !this.sentinel || this.totalPages <= 1) {
        this.hideSentinel();
        return;
      }

      this.initObserver();
    }

    initObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0]?.isIntersecting || this.loading || this.currentPage >= this.totalPages) {
            return;
          }
          this.loadNextPage();
        },
        { rootMargin: '200px 0px' }
      );

      this.observer.observe(this.sentinel);
    }

    hideSentinel() {
      if (this.sentinel) this.sentinel.hidden = true;
      if (this.loader) this.loader.hidden = true;
    }

    setLoading(isLoading) {
      this.loading = isLoading;
      if (this.loader) this.loader.hidden = !isLoading;
      this.root.dataset.loading = isLoading ? 'true' : 'false';
    }

    announce(message) {
      if (this.status) this.status.textContent = message;
    }

    async loadNextPage() {
      if (this.loading || this.currentPage >= this.totalPages) return;

      const nextPage = this.currentPage + 1;
      this.setLoading(true);

      try {
        const url = new URL(this.blogPath, window.location.origin);
        url.searchParams.set('page', String(nextPage));
        url.searchParams.set('section_id', this.sectionId);

        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`Section fetch failed (${response.status})`);

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = doc.querySelector('[data-blog-articles-grid]');

        if (!nextGrid) throw new Error('Grid markup missing from section response');

        const cards = nextGrid.querySelectorAll('.preptera-blog-card');
        if (cards.length === 0) {
          this.announce('No more articles to load.');
          this.hideSentinel();
          this.observer?.disconnect();
          return;
        }

        cards.forEach((card) => {
          const link = card.querySelector('.preptera-blog-card__media-link, .preptera-blog-card__title a');
          const href = link?.getAttribute('href') || '';
          const handle = href.split('/').filter(Boolean).pop() || '';

          if (this.featuredHandle && handle === this.featuredHandle) {
            return;
          }

          this.grid.appendChild(document.importNode(card, true));
        });

        window.prepteraReveal?.scan(this.grid);

        this.currentPage = nextPage;
        this.root.dataset.currentPage = String(nextPage);

        if (this.currentPage >= this.totalPages) {
          this.announce('All articles loaded.');
          this.hideSentinel();
          this.observer?.disconnect();
        } else {
          this.announce(`Loaded page ${nextPage} of ${this.totalPages}.`);
        }
      } catch (error) {
        console.error('[preptera-blog-articles]', error);
        this.announce('Unable to load more articles. Scroll again to retry.');
      } finally {
        this.setLoading(false);
      }
    }
  }

  function init() {
    document.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.dataset.blogArticlesInit === 'true') return;
      root.dataset.blogArticlesInit = 'true';
      new PrepteraBlogArticles(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
